import React, { useState } from 'react';
import VerifyEmail from './VerifyEmail';

type TestScenario = {
    name: string;
    status: 'verifying' | 'success' | 'error';
    error?: string;
    description: string;
};

const testScenarios: TestScenario[] = [
    {
        name: 'Loading State',
        status: 'verifying',
        description: 'Shows the loading spinner while verification is in progress'
    },
    {
        name: 'Success State',
        status: 'success',
        description: 'Shows successful verification message with auto-redirect'
    },
    {
        name: 'Generic Error',
        status: 'error',
        error: 'Failed to verify email',
        description: 'Shows a generic verification failure message'
    },
    {
        name: 'Invalid Token',
        status: 'error',
        error: 'Invalid verification token provided.',
        description: 'Shows error when token is malformed or invalid'
    },
    {
        name: 'Expired Token',
        status: 'error',
        error: 'Verification token has expired. Please request a new verification email.',
        description: 'Shows error when token has expired'
    },
    {
        name: 'Server Error',
        status: 'error',
        error: 'Server is currently unavailable. Please try again later.',
        description: 'Shows error when server is down or unreachable'
    },
    {
        name: 'Network Error',
        status: 'error',
        error: 'Network connection failed. Please check your internet connection.',
        description: 'Shows error when network request fails'
    }
];

const VerifyEmailTest: React.FC = () => {
    const [selectedScenario, setSelectedScenario] = useState<TestScenario>(testScenarios[0]);

    return (
        <div style={{ display: 'flex', height: '100vh' }}>
            {/* Left Panel - Test Controls */}
            <div style={{
                width: '400px',
                padding: '20px',
                backgroundColor: '#f5f5f5',
                borderRight: '1px solid #ddd',
                overflowY: 'auto'
            }}>
                <h2>Email Verification Test</h2>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                    This page allows you to test different email verification states and scenarios.
                </p>

                <div style={{ marginBottom: '20px' }}>
                    <h3>Test Scenarios</h3>
                    {testScenarios.map((scenario, index) => (
                        <div key={index} style={{ marginBottom: '10px' }}>
                            <button
                                onClick={() => setSelectedScenario(scenario)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    textAlign: 'left',
                                    border: '1px solid #ddd',
                                    backgroundColor: selectedScenario.name === scenario.name ? '#007bff' : 'white',
                                    color: selectedScenario.name === scenario.name ? 'white' : 'black',
                                    cursor: 'pointer',
                                    borderRadius: '4px'
                                }}
                            >
                                <strong>{scenario.name}</strong>
                                <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.8 }}>
                                    {scenario.description}
                                </div>
                            </button>
                        </div>
                    ))}
                </div>

                <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#e9ecef', 
                    borderRadius: '4px',
                    fontSize: '14px'
                }}>
                    <strong>Current Test:</strong>
                    <div>Status: {selectedScenario.status}</div>
                    {selectedScenario.error && <div>Error: {selectedScenario.error}</div>}
                </div>

                <div style={{ marginTop: '20px', fontSize: '12px', color: '#666' }}>
                    <p><strong>Note:</strong> This test page is only available in development mode.</p>
                    <p>In production, users will only see the actual verification page at <code>/verify-email</code>.</p>
                </div>
            </div>

            {/* Right Panel - Verification Component */}
            <div style={{ flex: 1 }}>
                <VerifyEmail 
                    testStatus={selectedScenario.status} 
                    testError={selectedScenario.error}
                />
            </div>
        </div>
    );
};

export default VerifyEmailTest; 